import { inject, injectable } from 'inversify';
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import DependencyType from '../../dependancyInjection/DependencyType';
import { ConfigurationService } from '../ConfigurationService/ConfigurationService';
import { DisplayOnlyTrackedHandleType } from './DisplayOnlyTrackedHandleType.enum';
import { LogUtil } from '../../utils/Logging.Util';
import {
    GenerateTrackedUrlDocument,
    GenerateTrackedUrlMutation,
    GenerateTrackedUrlMutationVariables,
    IsTrackedUrlScannedDocument,
    IsTrackedUrlScannedQuery,
    IsTrackedUrlScannedQueryVariables,
} from '../../provider/cloudshelf/graphql/generated/cloudshelf_types';

export interface TrackedUrl {
    url: string;
    id: string;
}

@injectable()
export class CloudshelfTrackedURLService {
    constructor(
        @inject(DependencyType.ApolloClient) private readonly _apolloClient: ApolloClient<NormalizedCacheObject>,
        @inject(DependencyType.ConfigurationService) private readonly _configService: ConfigurationService,
    ) {}

    generateDisplayOnlyURL(handleType: DisplayOnlyTrackedHandleType, handleOrLink: string): string | undefined {
        try {
            const apiHost = process.env.REACT_APP_BACKEND_HOST;
            const cloudshelfId = btoa(this._configService.cloudshelfId ?? '');
            handleOrLink = btoa(handleOrLink);

            if (!apiHost) {
                LogUtil.LogException('Unable to create a display transfer tracked URL.', new Error('Missing api host'));
                return undefined;
            }

            if (!cloudshelfId) {
                LogUtil.LogException(
                    'Unable to create a display transfer tracked URL.',
                    new Error('Missing cloudshelf id'),
                );
                return undefined;
            }

            let url: string | undefined;

            if (handleType === DisplayOnlyTrackedHandleType.PRODUCT) {
                url = `${apiHost}/rest/trackedURLs/displayTransfer/${cloudshelfId}/product/${handleOrLink}`;
            } else if (handleType === DisplayOnlyTrackedHandleType.CATEGORY) {
                url = `${apiHost}/rest/trackedURLs/displayTransfer/${cloudshelfId}/category/${handleOrLink}`;
            } else if (handleType === DisplayOnlyTrackedHandleType.BANNER_LINK) {
                url = `${apiHost}/rest/trackedURLs/displayTransfer/${cloudshelfId}/bannerLink/${encodeURIComponent(
                    handleOrLink,
                )}`;
            }

            return url;
        } catch (err) {
            LogUtil.LogException('Unable to create a display transfer tracked URL.', err);
            return undefined;
        }
    }

    async generateTrackedURL(urlToTrack: string, sessionId?: string): Promise<TrackedUrl | undefined> {
        try {
            const mutationTuple = await this._apolloClient.mutate<
                GenerateTrackedUrlMutation,
                GenerateTrackedUrlMutationVariables
            >({
                mutation: GenerateTrackedUrlDocument,
                context: { timeout: 4000 },
                variables: {
                    cloudshelfId: this._configService.config()?.id ?? '',
                    url: urlToTrack,
                    sessionId: sessionId,
                },
            });

            if (mutationTuple.data?.createTrackedURL.trackedUrl) {
                LogUtil.Log(`Generated tracked URL: ${mutationTuple.data?.createTrackedURL.trackedUrl.url}`);
                return {
                    url: mutationTuple.data.createTrackedURL.trackedUrl.url,
                    id: mutationTuple.data.createTrackedURL.trackedUrl.id,
                };
            }

            return undefined;
        } catch {
            // Error will throw if timeout occurs. Don't need sentry as timeout is likely to just mean bad wifi.
            return undefined;
        }
    }

    async isTrackedUrlQRScanned(id: string): Promise<boolean> {
        const queryTuple = await this._apolloClient.query<IsTrackedUrlScannedQuery, IsTrackedUrlScannedQueryVariables>({
            query: IsTrackedUrlScannedDocument,
            variables: {
                id,
            },
        });

        console.log('isTrackedUrlQRScanned', queryTuple.data?.isTrackedURLScanned, id);

        return !(queryTuple.errors || !queryTuple.data.isTrackedURLScanned);
    }
}
